/**
 * Drafts kept on this device, in IndexedDB. PLAN.md 3.3.
 *
 * One record per document, keyed by (user, project, template, document), so
 * one executive can have many drafts across projects and each resumes on its
 * own. This is where Aadhaar/PAN/TAN values and ID card photos live: they
 * never leave the device. It is interruption resilience only; there is no
 * offline mode.
 *
 * If the browser's storage is cleared, the Tier 2 values come back from the
 * server draft; the ID numbers and photos must be entered again.
 *
 * Owner 10-21: a finished ('completed') record keeps the FULL fill and its
 * photos too, so it can be downloaded again without typing, until the
 * registration number is entered; then the record and its photos are deleted.
 */
import type { Fill } from '@saledeed/shared';
import type { PhotoRef } from './fill-state';

export interface LocalDraft {
  key: string;
  userId: string;
  projectId: string;
  templateCode: string;
  documentId: string;
  projectName: string;
  templateTitle: string;
  /** The FULL fill, ID numbers included. */
  fill: Fill;
  /** Server revision this fill was last saved as. */
  baseRevision: number;
  /** Changes here that the server does not have yet. */
  dirty: boolean;
  state: 'draft' | 'completed';
  /** Seller → buyer names, for this device's own list. */
  names: string;
  pdfSha256?: string;
  updatedAt: string;
}

/** An ID card photo, already scaled down and re-encoded (see photos.ts). */
export interface Photo extends PhotoRef {
  dataUrl: string;
  width: number;
  height: number;
}

export interface DraftStore {
  get(key: string): Promise<LocalDraft | undefined>;
  put(draft: LocalDraft): Promise<void>;
  /** Deletes the draft and its photos. */
  delete(key: string): Promise<void>;
  list(userId: string): Promise<LocalDraft[]>;
  getPhotos(key: string): Promise<Photo[]>;
  putPhotos(key: string, photos: Photo[]): Promise<void>;
}

/** The app's storage is held at an older version by another open tab of the app. */
export class StorageBlockedError extends Error {
  constructor() {
    super('Another tab of this app is still open from before the update. Close that tab (or reload it), then reload this page, so this computer can keep your draft, ID numbers and photos.');
    this.name = 'StorageBlockedError';
  }
}

/** Resolves with the store's answer, or rejects after `ms` (storage must never freeze the page). */
export function withTimeout<T>(p: Promise<T>, ms = 5000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new StorageBlockedError()), ms);
    p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
  });
}

export function draftKey(userId: string, projectId: string, templateCode: string, documentId: string) {
  return [userId, projectId, templateCode, documentId].join('|');
}

/* ---------------------------------------------------------- IndexedDB */

const DB_NAME = 'saledeed';
const STORE = 'drafts';
const PHOTOS = 'photos';

function request<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('Browser storage failed'));
  });
}

export function indexedDbStore(): DraftStore {
  let dbp: Promise<IDBDatabase> | null = null;
  const open = () => {
    dbp ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 2);
      let blockedTimer: ReturnType<typeof setTimeout> | undefined;
      req.onupgradeneeded = ev => {
        const db = req.result;
        if (ev.oldVersion < 1) db.createObjectStore(STORE, { keyPath: 'key' }).createIndex('userId', 'userId');
        if (ev.oldVersion < 2) db.createObjectStore(PHOTOS, { keyPath: 'key' });
      };
      req.onsuccess = () => {
        clearTimeout(blockedTimer);
        const db = req.result;
        // A newer version of the app in another tab needs to upgrade: let it.
        db.onversionchange = () => { db.close(); dbp = null; };
        resolve(db);
      };
      req.onerror = () => { clearTimeout(blockedTimer); dbp = null; reject(req.error ?? new Error('Browser storage is not available')); };
      // Another tab still holds the older version: the upgrade waits until it closes. Do not
      // hang the page; say so, and try again next time.
      req.onblocked = () => {
        blockedTimer = setTimeout(() => { dbp = null; reject(new StorageBlockedError()); }, 3000);
      };
    });
    return dbp;
  };
  const tx = async (store: string, mode: IDBTransactionMode) => (await open()).transaction(store, mode).objectStore(store);
  return {
    get: async key => request((await tx(STORE, 'readonly')).get(key)) as Promise<LocalDraft | undefined>,
    put: async draft => { await request((await tx(STORE, 'readwrite')).put(draft)); },
    delete: async key => {
      await request((await tx(STORE, 'readwrite')).delete(key));
      await request((await tx(PHOTOS, 'readwrite')).delete(key));
    },
    list: async userId => {
      const all = await request((await tx(STORE, 'readonly')).index('userId').getAll(userId)) as LocalDraft[];
      return all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },
    getPhotos: async key => ((await request((await tx(PHOTOS, 'readonly')).get(key))) as { photos: Photo[] } | undefined)?.photos ?? [],
    putPhotos: async (key, photos) => {
      const store = await tx(PHOTOS, 'readwrite');
      if (photos.length) await request(store.put({ key, photos }));
      else await request(store.delete(key));
    },
  };
}

/** For tests, and for browsers without IndexedDB (then nothing survives a reload). */
export function memoryStore(): DraftStore {
  const map = new Map<string, LocalDraft>();
  const photos = new Map<string, Photo[]>();
  return {
    get: async key => (map.has(key) ? structuredClone(map.get(key)!) : undefined),
    put: async draft => { map.set(draft.key, structuredClone(draft)); },
    delete: async key => { map.delete(key); photos.delete(key); },
    list: async userId => [...map.values()].filter(d => d.userId === userId).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).map(d => structuredClone(d)),
    getPhotos: async key => structuredClone(photos.get(key) ?? []),
    putPhotos: async (key, list) => { if (list.length) photos.set(key, structuredClone(list)); else photos.delete(key); },
  };
}

let current: DraftStore | null = null;

export function draftStore(): DraftStore {
  current ??= typeof indexedDB === 'undefined' ? memoryStore() : indexedDbStore();
  return current;
}

export function setDraftStore(store: DraftStore) {
  current = store;
}
